import React, { useEffect, useState } from "react";
import { Calendar, Loader, Upload, X } from "lucide-react";
import { useAlert } from "components/UI_components/Alert";
import { BASE_URL } from "utils/constant";
import { resolveTransactionFeedback } from "utils/transactionFeedback";

export default function RecievedPaymentForm({ onClose }) {
  const [transactionType, setTransactionType] = useState("");
  const [amount, setAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("Cash");
  const [remarks, setRemarks] = useState("");
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isLoading, setIsLoading] = useState(false);
  const [isSaveDisabled, setIsSaveDisabled] = useState(false);
  const [receivedTransactionCatagory, setReceivedTransactionCatagory] =
    useState(null);
  const { addAlert } = useAlert();
  const transactionTypes = [
    "Equipments",
    "Electricity",
    "Transport",
    "Municipality/Panchayat",
    "Water Bill",
  ];

  const formatDateForDisplay = (date) => {
    return `${date.getDate().toString().padStart(2, "0")}/${(
      date.getMonth() + 1
    )
      .toString()
      .padStart(2, "0")}/${date.getFullYear()}`;
  };

  const formatDateForAPI = (date) => {
    return `${date.getFullYear()}-${(date.getMonth() + 1)
      .toString()
      .padStart(2, "0")}-${date.getDate().toString().padStart(2, "0")}`;
  };

  useEffect(() => {
    const fetchTransactionCatagories = async () => {
      try {
        const accessToken = localStorage.getItem("access");
        const response = await fetch(
          `${BASE_URL}/finance/get-all-finance-catagory/`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
            },
          },
        );
        if (response.ok) {
          const res = await response.json();
          const data = res.data;
          const purchaseCatagory = data.find(
            (cat) => cat.catagory_name === "Received",
          );
          if (purchaseCatagory) {
            setReceivedTransactionCatagory(purchaseCatagory.id);
          }
        } else {
          console.error("Failed to fetch material transaction catagories");
        }
      } catch (error) {
        console.error("Error fetching material transaction catagories:", error);
      }
    };
    fetchTransactionCatagories();
  }, []);

  const handleSave = async () => {
    if (!transactionType || !amount || isSaveDisabled) return;

    setIsLoading(true);
    setIsSaveDisabled(true); // Disable save button immediately after click

    const site = localStorage.getItem("site");
    const formattedDate = formatDateForAPI(selectedDate);

    const paymentData = {
      transaction_type: transactionType,
      transaction_date: formattedDate,
      amount: parseInt(amount),
      transaction_method: paymentMethod,
      comments: remarks,
      receipt_url: "",
      transaction_catagory_id: receivedTransactionCatagory,
      site: parseInt(site),
    };

    try {
      const accessToken = localStorage.getItem("access");
      const response = await fetch(
        `${BASE_URL}/finance/create-general-transaction/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(paymentData),
        },
      );
      const data = await response.json();
      const feedback = resolveTransactionFeedback(data, {
        successMessage: "Payment created successfully",
        errorMessage: "Failed to create payment",
      });

      if (feedback.success) {
        if (feedback.approvalRequired) {
          addAlert(feedback.message, feedback.alertType);
        }
        onClose();
      } else {
        addAlert(feedback.message, feedback.alertType);
        // Re-enable button if API call fails
        setIsSaveDisabled(false);
      }
    } catch (error) {
      console.error("Error creating payment:", error);
      addAlert(error.message || "Failed to create payment", "error");
      // Re-enable button if API call fails
      setIsSaveDisabled(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetNow = () => {
    setSelectedDate(new Date());
    setShowDatePicker(false);
  };

  return (
    <div className="w-full h-full px-2 pt-1 pb-0 bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="relative flex items-center justify-center mb-1 px-1 py-1">
        <h1 className="text-[20px] font-semibold text-gray-900 text-center">
          Received
        </h1>
        <button
          onClick={onClose}
          className="absolute -top-1 right-0 p-1.5 rounded-full hover:bg-gray-100 text-gray-700 transition-colors"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Main Form */}
      <div className="flex-1 overflow-y-auto pr-1">
        <div className="grid grid-cols-1 gap-3">
        {/* Purpose of Transaction */}
        <div className="bg-white p-3 rounded-lg shadow-sm">
          <label className="block text-xs text-gray-600 mb-1">
            Purpose of Transaction
          </label>
          <input
            type="text"
            className="w-full p-2.5 text-sm bg-white rounded-lg border border-gray-200 mb-2"
            placeholder="Type"
            value={transactionType}
            onChange={(e) => setTransactionType(e.target.value)}
          />
          <div className="flex flex-wrap gap-2">
            {transactionTypes.map((type) => (
              <button
                key={type}
                onClick={() => setTransactionType(type)}
                className={`px-3 py-1.5 rounded-full text-xs ${
                  transactionType === type
                    ? "bg-purple-100 text-purple-600"
                    : "bg-gray-100 text-gray-600"
                }`}
              >
                {type}
              </button>
            ))}
          </div>
        </div>

        {/* Left and Right Side Sections */}
        <div className="grid grid-cols-1 gap-3">
          {/* Left Side */}
          <div className="space-y-3">
            {/* Payment Date */}
            <div className="bg-white p-3 rounded-lg shadow-sm">
              <label className="block text-xs text-gray-600 mb-1">
                Payment Date
              </label>
              <div className="flex gap-2">
                <button
                  className={`px-3 py-1.5 rounded-full text-xs ${
                    !showDatePicker
                      ? "bg-purple-100 text-purple-600"
                      : "bg-gray-100 text-gray-600"
                  }`}
                  onClick={handleSetNow}
                >
                  Now
                </button>
                <button
                  className={`px-3 py-1.5 rounded-full text-xs flex items-center gap-2 ${
                    showDatePicker
                      ? "bg-purple-100 text-purple-600"
                      : "bg-gray-100 text-gray-600"
                  }`}
                  onClick={() => setShowDatePicker(!showDatePicker)}
                >
                  Pick an older date
                  <Calendar className="h-4 w-4" />
                </button>
              </div>

              {showDatePicker && (
                <div className="mt-3 p-2 border border-gray-200 rounded-lg">
                  <div className="text-xs text-gray-600 mb-2">
                    {formatDateForDisplay(selectedDate)}
                  </div>
                  <input
                    type="date"
                    className="w-full p-2 border border-gray-200 rounded-md"
                    value={formatDateForAPI(selectedDate)}
                    max={formatDateForAPI(new Date())}
                    onChange={(e) => {
                      if (e.target.value) {
                        setSelectedDate(new Date(e.target.value));
                      }
                    }}
                  />
                </div>
              )}
            </div>

            {/* Amount and Payment Method */}
            <div className="bg-white p-3 rounded-lg space-y-3 shadow-sm">
              <div>
                <label className="block text-xs text-gray-600 mb-1">
                  ₹ Amount
                </label>
                <input
                  type="text"
                  className="w-full p-2.5 text-sm bg-white rounded-lg border border-gray-200"
                  placeholder="Enter amount"
                  value={amount}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === "" || parseFloat(val) >= 0) {
                      setAmount(val);
                    }
                  }}
                />
              </div>

              <div>
                <label className="block text-xs text-gray-600 mb-1">
                  Payment Method
                </label>
                <div className="flex flex-wrap gap-2">
                  {["Cash", "UPI/Bank Transfer", "Cheque"].map((method) => (
                    <button
                      key={method}
                      className={`px-3 py-1.5 rounded-full text-xs ${
                        paymentMethod === method
                          ? "bg-purple-100 text-purple-600"
                          : "bg-gray-100 text-gray-600"
                      }`}
                      onClick={() => setPaymentMethod(method)}
                    >
                      {method}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Right Side */}
          <div className="space-y-3">
            {/* Remarks */}
            <div className="bg-white p-3 rounded-lg shadow-sm">
              <input
                type="text"
                className="w-full p-2 text-sm bg-transparent border-b border-gray-300"
                placeholder="Remarks / Comments (Optional)"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
              />
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* Save Button */}
      <button
        className={`w-full mt-2 py-2.5 text-sm rounded-md font-medium transition duration-300 flex items-center justify-center gap-2 shrink-0 ${
          isLoading || isSaveDisabled || !transactionType || !amount
            ? "bg-gray-400 text-gray-700 cursor-not-allowed"
            : "bg-[#3B0069] text-white hover:bg-purple-700"
        }`}
        onClick={handleSave}
        disabled={isLoading || isSaveDisabled || !transactionType || !amount}
      >
        {isLoading ? (
          <>
            <Loader className="h-5 w-5 animate-spin" />
            Saving...
          </>
        ) : isSaveDisabled ? (
          "Saved"
        ) : (
          "Save"
        )}
      </button>
    </div>
  );
}
